import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTrophy, FaUsers, FaFutbol,
  FaCheckCircle, FaChevronRight, FaChevronLeft,
  FaCopy, FaLock, FaMedal, FaSpinner, FaExclamationTriangle,
  FaTimes,
} from 'react-icons/fa';
import { formatMatchDate } from '../utils/formatMatchDate';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import './ChampionshipPage.css';

/* ── Static display content (kept from original) ── */
const AFFIL_STEPS = [
  {
    icon: <FaUsers />,
    title: 'Invitez 30 amis',
    desc: 'Partagez votre lien de parrainage. Chaque ami qui s\'inscrit avec votre lien compte comme un filleul.',
  },
  {
    icon: <FaTrophy />,
    title: 'Accès au Champion Contest débloqué',
    desc: 'Dès que vous atteignez 30 filleuls, le Champion Contest s\'ouvre automatiquement pour vous.',
  },
];

/* ────────────────────────────────────────────
   Sub-components
──────────────────────────────────────────── */

/* Spinner helper */
function Spinner() {
  return <FaSpinner className="spin-icon" />;
}

/* ── Team logo — simple img with onError fallback ── */
const FALLBACK_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='6' fill='%231a2e1f'/%3E%3Ccircle cx='20' cy='20' r='12' fill='none' stroke='%231db954' stroke-width='2'/%3E%3Cpath d='M20 12 L24 18 L20 22 L16 18 Z' fill='%231db954' opacity='0.7'/%3E%3C/svg%3E`;

function TeamLogo({ src, name, size = 40 }) {
  return (
    <img
      src={src || FALLBACK_SVG}
      alt={name || ''}
      width={size}
      height={size}
      className="champ-team-flag"
      onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_SVG; }}
    />
  );
}

/* ── Progress / locked page ── */
const APP_URL = 'https://afri-bet.org';

function ProgressPage({ referralData }) {
  const { referral_code, qualified_referrals } = referralData;
  const GOAL = 30;
  const pct  = Math.min(100, Math.round((qualified_referrals / GOAL) * 100));
  const left = GOAL - qualified_referrals;

  const referralLink = `${APP_URL}/register?ref=${referral_code}`;

  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(referral_code).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    });
  };
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };
  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Rejoins-moi sur AFRIBET et parie sur la World Cup ! Inscris-toi avec mon lien : ${referralLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="champ-page">

      {/* Hero */}
      <section className="champ-hero">
        <div className="champ-hero-inner">
          <div className="champ-hero-badge">
            <FaLock />
            <span>Champion Contest — Accès verrouillé</span>
          </div>
          <h1 className="champ-hero-title">
            Pariez. Gagnez.<br />
            <span className="champ-accent">Devenez Champion.</span>
          </h1>
          <p className="champ-hero-sub">
            Invitez <strong>{GOAL} amis</strong> pour débloquer l'accès aux concours exclusifs
            avec des cagnottes partagées.
          </p>
        </div>
      </section>

      {/* Progress card */}
      <section className="champ-section">
        <div className="champ-progress-card">
          <div className="champ-prog-header">
            <div>
              <p className="champ-prog-label">Votre progression</p>
              <p className="champ-prog-count">
                <span className="champ-prog-done">{qualified_referrals}</span>
                <span className="champ-prog-sep"> / </span>
                <span className="champ-prog-total">{GOAL}</span>
                <span className="champ-prog-unit"> filleuls</span>
              </p>
            </div>
            <div className="champ-prog-pct">{pct}%</div>
          </div>

          <div className="champ-prog-bar-wrap">
            <div className="champ-prog-bar">
              <div className="champ-prog-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {left > 0 ? (
            <p className="champ-prog-msg">
              🎯 Encore <strong>{left} filleul{left > 1 ? 's' : ''}</strong> pour débloquer le Champion Contest
            </p>
          ) : (
            <p className="champ-prog-msg champ-prog-almost">
              🎉 Félicitations ! En attente de validation serveur…
            </p>
          )}
        </div>
      </section>

      {/* Share section */}
      <section className="champ-section">
        <div className="champ-section-header">
          <span className="champ-eyebrow">Partagez et progressez</span>
          <h2>Votre lien & code de parrainage</h2>
          <p>Chaque ami qui s'inscrit avec votre lien ou code compte comme un filleul — c'est tout.</p>
        </div>

        {/* Referral link */}
        <div className="champ-share-card">
          <div className="champ-share-row">
            <div className="champ-share-label-wrap">
              <span className="champ-share-label">Lien d'invitation</span>
            </div>
            <div className="champ-share-link-box">
              <span className="champ-share-link-text">{referralLink}</span>
              <button className="champ-share-copy-btn" onClick={copyLink}>
                {copiedLink ? <FaCheckCircle /> : <FaCopy />}
                {copiedLink ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>

          <div className="champ-share-divider" />

          <div className="champ-share-row">
            <div className="champ-share-label-wrap">
              <span className="champ-share-label">Code de parrainage</span>
            </div>
            <div className="champ-ref-box">
              <span className="champ-ref-code">{referral_code}</span>
              <button className="champ-ref-copy" onClick={copyCode}>
                {copiedCode ? <FaCheckCircle /> : <FaCopy />}
                {copiedCode ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>

          <div className="champ-share-actions">
            <button className="champ-whatsapp-btn" onClick={shareWhatsApp}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Partager sur WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="champ-section">
        <div className="champ-section-header">
          <span className="champ-eyebrow">Comment ça marche</span>
          <h2>Comment débloquer le Champion Contest ?</h2>
        </div>
        <div className="champ-steps-row">
          {AFFIL_STEPS.map((s, i) => (
            <React.Fragment key={s.title}>
              <div className="champ-step-card">
                <div className="champ-step-icon">{s.icon}</div>
                <div className="champ-step-num">Étape {i + 1}</div>
                <h3 className="champ-step-title">{s.title}</h3>
                <p className="champ-step-desc">{s.desc}</p>
              </div>
              {i < AFFIL_STEPS.length - 1 && <div className="champ-step-arrow">›</div>}
            </React.Fragment>
          ))}
        </div>
      </section>

    </div>
  );
}

/* ── Contest list ── */
function ContestList({ contests, onSelect }) {
  if (contests.length === 0) {
    return (
      <div className="champ-empty">
        <span className="champ-empty-icon">🏆</span>
        <p>Aucun concours actif pour le moment. Revenez bientôt !</p>
      </div>
    );
  }

  return (
    <div className="champ-contests-grid">
      {contests.map((contest) => (
        <div className="champ-contest-card" key={contest.id}>
          <div className="champ-contest-top">
            <div className="champ-contest-status">
              <span className="champ-status-dot" />
              {contest.status === 'active' ? 'En cours' : contest.status}
            </div>
            <div className="champ-contest-prize">
              <FaTrophy className="champ-prize-icon" />
              {parseFloat(contest.prize_amount).toLocaleString('fr-FR')} FC
            </div>
          </div>

          <h3 className="champ-contest-title">{contest.title}</h3>

          <div className="champ-contest-meta">
            <span>
              <FaFutbol style={{ marginRight: 5 }} />
              {contest.matches?.length ?? 0} match{(contest.matches?.length ?? 0) > 1 ? 's' : ''}
            </span>
            <span>
              Fin : {new Date(contest.end_date).toLocaleDateString('fr-FR', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </span>
          </div>

          {/* Mini match preview */}
          {contest.matches && contest.matches.length > 0 && (
            <div className="champ-contest-preview">
              {contest.matches.slice(0, 3).map((m) => (
                <div className="champ-preview-match" key={m.id}>
                  <span className="champ-preview-team">{m.fixture?.home_team}</span>
                  <span className="champ-preview-vs">vs</span>
                  <span className="champ-preview-team">{m.fixture?.away_team}</span>
                </div>
              ))}
              {contest.matches.length > 3 && (
                <p className="champ-preview-more">+{contest.matches.length - 3} autres matchs</p>
              )}
            </div>
          )}

          <button className="champ-btn-primary champ-contest-btn" onClick={() => onSelect(contest)}>
            Participer <FaChevronRight size={11} />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── Contest detail + prediction form ── */
function ContestDetail({ contest: initialContest, user, accessToken, onBack }) {

  const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

  /* ── State ── */
  const [contest,      setContest]      = useState(initialContest);
  const [myPreds,      setMyPreds]      = useState([]);
  const [loading,      setLoading]      = useState(true);   // initial parallel load
  const [loadError,    setLoadError]    = useState(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeTab,    setActiveTab]    = useState('predict');

  const [predictions,  setPredictions]  = useState({});
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState(null);

  const [leaderboard,  setLeaderboard]  = useState([]);
  const [loadingLb,    setLoadingLb]    = useState(false);
  const [lbError,      setLbError]      = useState(null);

  /* ── Step 1 — load contest detail + my predictions in parallel ── */
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      fetch(`${API_BASE_URL}/api/v1/champion/contests/${initialContest.id}`, { headers })
        .then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE_URL}/api/v1/champion/contests/${initialContest.id}/predictions/mine`, { headers })
        .then(r => r.ok ? r.json() : []),
    ])
      .then(([contestData, predsData]) => {
        const c    = contestData || initialContest;
        const preds = Array.isArray(predsData) ? predsData : [];

        setContest(c);
        setMyPreds(preds);

        const submitted = preds.length > 0;
        setHasSubmitted(submitted);

        // Init prediction form scores
        const init = {};
        (c.matches || []).forEach((m) => {
          const fid = m.fixture?.id ?? m.id;
          const existing = preds.find(p => p.fixture_id === fid);
          init[fid] = {
            home: existing ? String(existing.predicted_home_score ?? 0) : '0',
            away: existing ? String(existing.predicted_away_score ?? 0) : '0',
          };
        });
        setPredictions(init);

        // If already submitted, go straight to results tab
        if (submitted) setActiveTab('results');
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContest.id, accessToken]);

  /* ── Step 2 — check if any match has already started ── */
  const hasMatchStarted = (contest.matches || []).some(
    m => m.fixture?.status && m.fixture.status !== 'NS'
  );

  /* ── Leaderboard ── */
  const loadLeaderboard = useCallback(async () => {
    setLoadingLb(true);
    setLbError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/champion/contests/${contest.id}/leaderboard`,
        { headers }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      setLeaderboard(await res.json());
    } catch (e) {
      setLbError(e.message);
    }
    setLoadingLb(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contest.id, accessToken]);

  useEffect(() => {
    if (activeTab === 'leaderboard') loadLeaderboard();
  }, [activeTab, loadLeaderboard]);

  /* ── Score stepper ── */
  const setScore = (fixtureId, side, val) => {
    if (!/^\d*$/.test(val)) return;
    setPredictions(prev => ({
      ...prev,
      [fixtureId]: { ...prev[fixtureId], [side]: val === '' ? '0' : val },
    }));
  };

  /* ── Step 3 — submit ── */
  const handleSubmit = async () => {
    setSubmitError(null);
    const preds = (contest.matches || []).map((m) => {
      const fid = m.fixture?.id ?? m.id;
      return {
        fixture_id: fid,
        predicted_home_score: parseInt(predictions[fid]?.home ?? '0', 10),
        predicted_away_score: parseInt(predictions[fid]?.away ?? '0', 10),
      };
    });

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/champion/contests/${contest.id}/predictions`,
        { method: 'POST', headers, body: JSON.stringify({ predictions: preds }) }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg  = body.detail || `Erreur ${res.status}`;

        // Step 4 — map backend error messages
        if (msg.includes('exactement')) {
          throw new Error('Vous devez prédire tous les matchs.');
        } else if (msg.includes("plus actif") || msg.includes("terminé")) {
          throw new Error('Ce concours est terminé.');
        } else if (msg.includes('fermées') || msg.includes('commencé')) {
          throw new Error(msg); // display backend message directly (contains team names)
        } else if (res.status === 403) {
          throw new Error('403'); // handled below
        }
        throw new Error(msg);
      }

      // ── Step 3: success → hide form immediately ──
      setHasSubmitted(true);
      setActiveTab('results');

      // Refresh my predictions for results tab
      const predsRes = await fetch(
        `${API_BASE_URL}/api/v1/champion/contests/${contest.id}/predictions/mine`,
        { headers }
      );
      if (predsRes.ok) setMyPreds(await predsRes.json());

    } catch (e) {
      if (e.message === '403') {
        onBack(); // redirect to progress page
      } else {
        setSubmitError(e.message);
      }
    }
    setSubmitting(false);
  };

  /* ── Merge match data with predictions for results tab ── */
  const matchesWithPreds = (contest.matches || []).map((m) => {
    const fid  = m.fixture?.id ?? m.id;
    const pred = myPreds?.find(p => p.fixture_id === fid);
    return { ...m, pred };
  });

  /* ── What to show in the predict tab ── */
  const renderPredictTab = () => {
    if (loading) return <div className="champ-loading"><Spinner /> Chargement…</div>;

    // Already submitted
    if (hasSubmitted) {
      return (
        <div className="champ-submitted-banner">
          <div className="champ-submitted-icon"><FaCheckCircle /></div>
          <div>
            <p className="champ-submitted-title">Prédictions soumises ✅</p>
            <p className="champ-submitted-sub">En attente des résultats des matchs. Consultez l'onglet Résultats.</p>
          </div>
          <button className="champ-btn-primary" onClick={() => setActiveTab('results')}>
            Voir mes résultats <FaChevronRight size={11} />
          </button>
        </div>
      );
    }

    // Predictions closed — match started
    if (hasMatchStarted) {
      return (
        <div className="champ-closed-banner">
          <div className="champ-closed-icon">🔒</div>
          <div>
            <p className="champ-closed-title">Prédictions fermées</p>
            <p className="champ-closed-sub">Un ou plusieurs matchs ont déjà commencé. Vous ne pouvez plus modifier vos prédictions.</p>
          </div>
        </div>
      );
    }

    // Show form
    return (
      <>
        <p className="champ-predict-hint">
          Entrez vos scores prédits pour chaque match. Vous pouvez modifier avant le début des matchs.
        </p>

        <div className="champ-matches-list">
          {(contest.matches || []).map((m, idx) => {
            const fid       = m.fixture?.id ?? m.id;
            const homeScore = parseInt(predictions[fid]?.home ?? '0', 10) || 0;
            const awayScore = parseInt(predictions[fid]?.away ?? '0', 10) || 0;
            const inc = (side) => setScore(fid, side, String(side === 'home' ? homeScore + 1 : awayScore + 1));
            const dec = (side) => {
              const cur = side === 'home' ? homeScore : awayScore;
              if (cur > 0) setScore(fid, side, String(cur - 1));
            };

            return (
              <div className="pm-card" key={m.id}>
                <div className="pm-date">
                  {formatMatchDate(m.fixture?.match_date) || `Match ${idx + 1}`}
                </div>
                <div className="pm-body">
                  <div className="pm-team">
                    <TeamLogo src={m.fixture?.home_logo} name={m.fixture?.home_team} size={56} />
                    <span className="pm-team-name">{m.fixture?.home_team}</span>
                  </div>
                  <div className="pm-stepper">
                    <div className="pm-stepper-col">
                      <button className="pm-step-btn" onClick={() => inc('home')} aria-label="Augmenter score domicile">&#8963;</button>
                      <span className="pm-score">{homeScore}</span>
                      <button className="pm-step-btn" onClick={() => dec('home')} aria-label="Diminuer score domicile" disabled={homeScore === 0}>&#8964;</button>
                    </div>
                    <div className="pm-stepper-col">
                      <button className="pm-step-btn" onClick={() => inc('away')} aria-label="Augmenter score extérieur">&#8963;</button>
                      <span className="pm-score">{awayScore}</span>
                      <button className="pm-step-btn" onClick={() => dec('away')} aria-label="Diminuer score extérieur" disabled={awayScore === 0}>&#8964;</button>
                    </div>
                  </div>
                  <div className="pm-team pm-team--right">
                    <TeamLogo src={m.fixture?.away_logo} name={m.fixture?.away_team} size={56} />
                    <span className="pm-team-name">{m.fixture?.away_team}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {submitError && (
          <div className="champ-error-banner"><FaExclamationTriangle /> {submitError}</div>
        )}

        <button
          className="champ-btn-primary champ-submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <><Spinner /> Envoi…</> : 'Soumettre mes prédictions'}
        </button>
      </>
    );
  };

  /* ── Render ── */
  if (loadError) {
    return (
      <div className="champ-page champ-page--center">
        <div className="champ-error-banner"><FaExclamationTriangle /> {loadError}</div>
      </div>
    );
  }

  return (
    <div className="champ-page">
      {/* Back + header */}
      <div className="champ-detail-header">
        <button className="champ-back-btn" onClick={onBack}>
          <FaChevronLeft /> Retour aux concours
        </button>
        <div className="champ-detail-meta">
          <h2 className="champ-detail-title">{contest.title}</h2>
          <span className="champ-detail-prize">
            <FaTrophy style={{ marginRight: 6 }} />
            {parseFloat(contest.prize_amount).toLocaleString('fr-FR')} FC
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="champ-tabs">
        {[
          { key: 'predict',     label: 'Mes prédictions' },
          { key: 'results',     label: 'Résultats'       },
          { key: 'leaderboard', label: 'Classement'      },
        ].map((t) => (
          <button
            key={t.key}
            className={`champ-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Predictions ── */}
      {activeTab === 'predict' && (
        <div className="champ-predict-section">
          {renderPredictTab()}
        </div>
      )}

      {/* ── TAB: Results ── */}
      {activeTab === 'results' && (
        <div className="champ-results-section">
          {loading ? (
            <div className="champ-loading"><Spinner /> Chargement…</div>
          ) : !myPreds || myPreds.length === 0 ? (
            <div className="champ-empty">
              <span className="champ-empty-icon">📋</span>
              <p>Vous n'avez pas encore soumis de prédictions pour ce concours.</p>
              <button className="champ-btn-primary" style={{ marginTop: 16 }} onClick={() => setActiveTab('predict')}>
                Entrer mes prédictions
              </button>
            </div>
          ) : (
            <div className="champ-results-list">
              {matchesWithPreds.map((m, idx) => {
                const pred        = m.pred;
                const hasRealScore = m.fixture?.home_score != null && m.fixture?.away_score != null;
                const verdict     = pred?.is_correct === true ? 'correct'
                                  : pred?.is_correct === false ? 'wrong'
                                  : 'pending';
                return (
                  <div className={`pm-card pm-card--result pm-card--${verdict}`} key={m.id}>
                    <div className="pm-result-header">
                      <span className="pm-date">
                        {formatMatchDate(m.fixture?.match_date) || `Match ${idx + 1}`}
                      </span>
                      {pred?.is_correct === true  && <span className="champ-verdict correct"><FaCheckCircle /> Correct</span>}
                      {pred?.is_correct === false  && <span className="champ-verdict wrong"><FaTimes /> Incorrect</span>}
                      {pred?.is_correct == null    && <span className="champ-verdict pending">⏳ En attente</span>}
                    </div>
                    <div className="pm-body">
                      <div className="pm-team">
                        <TeamLogo src={m.fixture?.home_logo} name={m.fixture?.home_team} size={56} />
                        <span className="pm-team-name">{m.fixture?.home_team}</span>
                      </div>
                      <div className="pm-result-scores">
                        <div className="pm-result-score-real">
                          <span className="pm-result-score-val">{hasRealScore ? m.fixture.home_score : '—'}</span>
                          <span className="pm-result-score-sep">:</span>
                          <span className="pm-result-score-val">{hasRealScore ? m.fixture.away_score : '—'}</span>
                        </div>
                        <span className="pm-result-score-label-real">{hasRealScore ? 'Résultat final' : 'À venir'}</span>
                        <div className="pm-result-divider" />
                        <div className="pm-result-score-pred">
                          <span className="pm-result-score-pred-val">{pred ? pred.predicted_home_score : '—'}</span>
                          <span className="pm-result-score-sep pm-result-score-sep--pred">:</span>
                          <span className="pm-result-score-pred-val">{pred ? pred.predicted_away_score : '—'}</span>
                        </div>
                        <span className="pm-result-score-label-pred">Votre prédit</span>
                      </div>
                      <div className="pm-team">
                        <TeamLogo src={m.fixture?.away_logo} name={m.fixture?.away_team} size={56} />
                        <span className="pm-team-name">{m.fixture?.away_team}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Leaderboard ── */}
      {activeTab === 'leaderboard' && (
        <div className="champ-lb-section">
          {loadingLb ? (
            <div className="champ-loading"><Spinner /> Chargement du classement…</div>
          ) : lbError ? (
            <div className="champ-error-banner"><FaExclamationTriangle /> {lbError}</div>
          ) : leaderboard.length === 0 ? (
            <div className="champ-empty">
              <span className="champ-empty-icon">🏅</span>
              <p>Aucun classement disponible pour l'instant.</p>
            </div>
          ) : (
            <div className="champ-lb-table">
              <div className="champ-lb-thead">
                <span>#</span><span>Joueur</span><span>Corrects</span><span>Total</span>
              </div>
              {leaderboard.map((entry) => {
                const isMe = entry.username === user?.username || entry.username === user?.name;
                return (
                  <div className={`champ-lb-row ${isMe ? 'champ-lb-row--me' : ''}`} key={entry.rank}>
                    <span className="champ-lb-rank">
                      {entry.rank === 1 ? <FaMedal className="champ-medal gold" />
                        : entry.rank === 2 ? <FaMedal className="champ-medal silver" />
                        : entry.rank === 3 ? <FaMedal className="champ-medal bronze" />
                        : entry.rank}
                    </span>
                    <span className="champ-lb-player">
                      {entry.username}
                      {isMe && <span className="champ-lb-me-badge">Vous</span>}
                    </span>
                    <span className="champ-lb-correct">{entry.correct}</span>
                    <span className="champ-lb-total">{entry.total}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────
   Main page component
──────────────────────────────────────────── */
export default function ChampionshipPage() {
  const { user, accessToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  /* Gate: must be logged in */
  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, navigate]);

  /* Step 1 — referral data */
  const [referralData, setReferralData]   = useState(null);
  const [loadingRef, setLoadingRef]       = useState(true);
  const [refError, setRefError]           = useState(null);

  /* Step 2 — contests */
  const [contests, setContests]           = useState([]);
  const [loadingContests, setLoadingContests] = useState(false);
  const [contestError, setContestError]   = useState(null);

  /* Navigation state */
  const [selectedContest, setSelectedContest] = useState(null);

  const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

  /* Fetch referral info */
  useEffect(() => {
    if (!accessToken) return;
    setLoadingRef(true);
    setRefError(null);
    fetch(`${API_BASE_URL}/api/v1/referral`, { headers })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setReferralData(data);
        setLoadingRef(false);
      })
      .catch((e) => {
        setRefError(e.message);
        setLoadingRef(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  /* Fetch contests when champion unlocked */
  useEffect(() => {
    if (!referralData?.is_champion_unlocked || !accessToken) return;
    setLoadingContests(true);
    setContestError(null);
    fetch(`${API_BASE_URL}/api/v1/champion/contests`, { headers })
      .then(async (res) => {
        if (res.status === 403) {
          // Server says not unlocked — re-render progress page
          setReferralData((prev) => ({ ...prev, is_champion_unlocked: false }));
          return [];
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setContests(Array.isArray(data) ? data : []);
        setLoadingContests(false);
      })
      .catch((e) => {
        setContestError(e.message);
        setLoadingContests(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralData?.is_champion_unlocked, accessToken]);

  /* ── Loading state ── */
  if (!isAuthenticated) return null;

  if (loadingRef) {
    return (
      <div className="champ-page champ-page--center">
        <Spinner /> Chargement…
      </div>
    );
  }

  if (refError) {
    return (
      <div className="champ-page champ-page--center">
        <div className="champ-error-banner"><FaExclamationTriangle /> {refError}</div>
      </div>
    );
  }

  /* ── Not unlocked → progress page ── */
  if (!referralData?.is_champion_unlocked) {
    return <ProgressPage referralData={referralData} />;
  }

  /* ── Contest detail view ── */
  if (selectedContest) {
    return (
      <ContestDetail
        contest={selectedContest}
        user={user}
        accessToken={accessToken}
        onBack={() => setSelectedContest(null)}
      />
    );
  }

  /* ── Contests list ── */
  return (
    <div className="champ-page">
      <section className="champ-hero">
        <div className="champ-hero-inner">
          <div className="champ-hero-badge">
            <FaTrophy />
            <span>Champion Contest — Accès débloqué</span>
          </div>
          <h1 className="champ-hero-title">
            Bienvenue,<br />
            <span className="champ-accent">Champion.</span>
          </h1>
          <p className="champ-hero-sub">
            Vous avez débloqué l'accès aux concours exclusifs AFRIBET.
            Prédisez les scores, battez vos adversaires, remportez la cagnotte.
          </p>
        </div>
      </section>

      <section className="champ-section">
        <div className="champ-section-header">
          <span className="champ-eyebrow">Concours en cours</span>
          <h2>Champion Contests actifs</h2>
          <p>Participez aux concours disponibles, entrez vos prédictions et visez la première place.</p>
        </div>

        {loadingContests && (
          <div className="champ-loading"><Spinner /> Chargement des concours…</div>
        )}
        {contestError && (
          <div className="champ-error-banner"><FaExclamationTriangle /> {contestError}</div>
        )}
        {!loadingContests && !contestError && (
          <ContestList contests={contests} onSelect={setSelectedContest} />
        )}
      </section>
    </div>
  );
}
